import type { FeatureSwitchName } from "@web-monorepo/feature-switches";

type ValidValues = "test" | "variant1" | "variant2" | "variant3" | "holdout";
type LoggedOutSwitchMap = {
  [K in FeatureSwitchName]?: {
    entityType: "parent" | "teacher";
    values: readonly ["off", "control", ValidValues, ...ValidValues[]] | readonly ["off", "on"];
    mobileOnly?: boolean;
  };
};

type LoggedInSwitchMap = {
  [K in FeatureSwitchName]?: {
    values: readonly ["off", "control", ValidValues, ...ValidValues[]] | readonly ["off", "on"];
    mobileOnly?: boolean;
  };
};

export const PARENT_REFERRALS_ENTRYPOINTS_SWITCH = "web_marketplace_parent_referrals_entrypoints";
export const WEB_MARKETPLACE_EXISTING_SUBSCRIPTION_RECOVERY_SWITCH = "web_marketplace_existing_subscription_recovery";
export const PARENT_REFERRALS_POSTCHECKOUT_SWITCH = "web_marketplace_referrals_postcheckout_May2026";
export const PARENT_REFERRAL_PDP_SHARING_SWITCH = "web_marketplace_parent_referral_pdp_sharing";
export const PARENT_LANGUAGE_SETTINGS_SWITCH = "web_marketplace_parent_language_settings";
export const PARENT_CHATS_SWITCH = "web_marketplace_parent_chats";
export const TUTOR_CHATS_SWITCH = "web_marketplace_tutor_chats";
export const TUTOR_COMMUNITY_SWITCH = "web_marketplace_tutor_community";
export const TUTOR_COURSE_THUMBNAIL_GENERATOR_SWITCH = "web_marketplace_tutor_course_thumbnail_generator";
export const COURSE_VIDEO_TUTORS_SWITCH = "web_marketplace_course_video_tutors";
export const COURSE_VIDEO_SEARCH_PDP_SWITCH = "web_marketplace_course_video_search_pdp";
export const TUTOR_SECTION_START_DATE_SWITCH = "web_marketplace_tutor_section_start_date";
export const GROUP_CLASS_STUDENT_TRANSFERS_SWITCH = "web_marketplace_groupclass_student_transfers";
export const TUTOR_REALTIME_TRANSCRIPTION_SWITCH = "web_marketplace_tutor_realtime_transcription";
export const PARENT_REALTIME_TRANSCRIPTION_SWITCH = "web_marketplace_parent_realtime_transcription";
export const PARENT_SECTION_START_DATE_SWITCH = "web_marketplace_parent_section_start_date";
export const AUTOMATICALLY_CANCEL_SECTIONS_ON_CONFLICT_SWITCH =
  "web_marketplace_automatically_cancel_sections_on_conflict";
export const TRIAL_QUIZ_FUNNEL_V4_EXPERIMENT = "web_flo_quiz_funnel_v4";
export const TUTOR_AUTO_MESSAGES_SWITCH = "api_marketplace_tutor_auto_messages";
export const TUTOR_SET_PRICES_SWITCH = "api_marketplace_tutor_set_prices";
export const HOMEPAGE_REDESIGN_EXPERIMENT = "web_marketplace_homepage_redesign_sep2026_2";
export const SHOW_PRICES_TUTOR_SEARCH_SWITCH = "web_marketplace_show_prices_tutor_search_aug2026";
export const PARENT_TSP_CHECKOUT_BREAKDOWN_SWITCH = "web_marketplace_parent_tsp_checkout_breakdown_aug2026";
export const SUBSCRIPTION_OUTCOME_VALUE_PRIMING_V2_EXPERIMENT = "web_marketplace_subscription_outcome_value_priming_v2";
export const PAYOUT_SHOW_FUTURE_SESSIONS_SWITCH = "web_payout_show_future_sessions";
export const GROUP_PDP_1_ON_1_FUNNEL_EXPERIMENT = "web_marketplace_1_1_Funnel_On_Group_Class_PDP_v1";
// Shared with teach/home/student — the messaging package gates the reaction picker on it.
export const MESSAGING_REACTIONS_SWITCH = "web_messaging_reactions";
export const MESSAGING_LINK_WARNING_SWITCH = "web_marketplace_show_messaging_link_warning_sept_2025";
export const LOGGED_OUT_SWITCH_MAP = {
  web_marketplace_normalize_typography_padding_margin: {
    entityType: "parent",
    values: ["off", "on"],
  },
  web_marketplace_show_diagnostic_link: {
    entityType: "parent",
    values: ["off", "on"],
  },
  web_marketplace_groupclass_crosssell_tutorsearch_Apr2026: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  web_marketplace_groupclass_homepagelink: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  [COURSE_VIDEO_SEARCH_PDP_SWITCH]: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  [PARENT_SECTION_START_DATE_SWITCH]: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  web_marketplace_lp_simplified_funnel: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  // off = default funnel, control = current modal funnel, test = personalized v4 quiz funnel
  [TRIAL_QUIZ_FUNNEL_V4_EXPERIMENT]: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  [PARENT_REFERRAL_PDP_SHARING_SWITCH]: {
    entityType: "parent",
    values: ["off", "on"],
  },
  [HOMEPAGE_REDESIGN_EXPERIMENT]: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  [SHOW_PRICES_TUTOR_SEARCH_SWITCH]: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
  [GROUP_PDP_1_ON_1_FUNNEL_EXPERIMENT]: {
    entityType: "parent",
    values: ["off", "control", "test"],
  },
} as const satisfies LoggedOutSwitchMap;

export const LOGGED_IN_SWITCH_MAP = {
  [TUTOR_AUTO_MESSAGES_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [WEB_MARKETPLACE_EXISTING_SUBSCRIPTION_RECOVERY_SWITCH]: {
    values: ["off", "on"],
  },
  web_marketplace_ed_consultant_reachouts: {
    values: ["off", "control", "holdout"],
  },
  web_marketplace_last_minute_sub_sept2025: {
    values: ["off", "control", "test"],
  },
  web_marketplace_last_minute_pubnub_sept2025: {
    values: ["off", "control", "test"],
  },
  web_marketplace_parent_billing: {
    values: ["off", "on"],
  },
  [PARENT_LANGUAGE_SETTINGS_SWITCH]: {
    values: ["off", "on"],
  },
  [PARENT_CHATS_SWITCH]: {
    values: ["off", "on"],
  },
  [TUTOR_CHATS_SWITCH]: {
    values: ["off", "on"],
  },
  [TUTOR_COMMUNITY_SWITCH]: {
    values: ["off", "on"],
  },
  [TUTOR_COURSE_THUMBNAIL_GENERATOR_SWITCH]: {
    values: ["off", "on"],
  },
  web_marketplace_show_pdp_buttons_in_profile_editor_nov2025: {
    values: ["off", "on"],
  },
  web_marketplace_yearly_recap: {
    values: ["off", "on"],
  },
  web_marketplace_decagon_for_tutors: {
    values: ["off", "control", "test"],
  },
  web_marketplace_decagon_for_parents: {
    values: ["off", "on"],
  },
  [PARENT_REFERRALS_ENTRYPOINTS_SWITCH]: {
    values: ["off", "on"],
  },
  [PARENT_REFERRALS_POSTCHECKOUT_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [PARENT_REFERRAL_PDP_SHARING_SWITCH]: {
    values: ["off", "on"],
  },
  web_marketplace_tutor_review_responses: {
    values: ["off", "on"],
  },
  web_marketplace_tutor_payouts: {
    values: ["off", "on"],
  },
  [PAYOUT_SHOW_FUTURE_SESSIONS_SWITCH]: {
    values: ["off", "on"],
  },
  web_marketplace_groupclass_internal_review: {
    values: ["off", "on"],
  },
  web_marketplace_tutor_substitute_toggle: {
    values: ["off", "on"],
  },
  [AUTOMATICALLY_CANCEL_SECTIONS_ON_CONFLICT_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [COURSE_VIDEO_TUTORS_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [TUTOR_SECTION_START_DATE_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [GROUP_CLASS_STUDENT_TRANSFERS_SWITCH]: {
    values: ["off", "on"],
  },
  [TUTOR_REALTIME_TRANSCRIPTION_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [PARENT_REALTIME_TRANSCRIPTION_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [PARENT_TSP_CHECKOUT_BREAKDOWN_SWITCH]: {
    values: ["off", "control", "test"],
  },
  [SUBSCRIPTION_OUTCOME_VALUE_PRIMING_V2_EXPERIMENT]: {
    values: ["off", "control", "test"],
  },
  [TUTOR_SET_PRICES_SWITCH]: {
    values: ["off", "control", "test"],
  },
  // Requested for both chats areas: the parent audience covers /parents/chats, the
  // tutor (teacher) audience covers /tutor/chats.
  [MESSAGING_REACTIONS_SWITCH]: {
    values: ["off", "on"],
  },
  [MESSAGING_LINK_WARNING_SWITCH]: {
    values: ["off", "control", "test"],
  },
} as const satisfies LoggedInSwitchMap;
